import React, { useState, useCallback, useRef } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase/firebaseConfig';
import './AddEventPage.css';

const DISTRICTS = [
  'All Kerala',
  'Thiruvananthapuram', 'Kollam', 'Pathanamthitta', 'Alappuzha',
  'Kottayam', 'Idukki', 'Ernakulam', 'Thrissur', 'Palakkad',
  'Malappuram', 'Calicut', 'Wayanad', 'Kannur', 'Kasaragod',
];

const STATUSES = ['upcoming', 'soon', 'completed', 'cancelled'];

const INITIAL = {
  title:          '',
  description:    '',
  location:       '',
  date:           '',
  time:           '',
  district:       '',
  status:         'upcoming',
};

export default function AddEventPage() {
  const [form,        setForm]       = useState(INITIAL);
  const [galleryUrls, setGallery]    = useState([]);
  const [urlInput,    setUrlInput]   = useState('');
  const [errors,      setErrors]     = useState({});
  const [submitting,  setSubmitting] = useState(false);
  const [toast,       setToast]      = useState(null);
  const urlInputRef = useRef(null);

  const [coverFile,    setCoverFile]    = useState(null);
  const [coverPreview, setCoverPreview] = useState('');
  const coverInputRef = useRef(null);

  const handleCoverChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setToast({ type: 'error', message: '❌ Please select an image file.' });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setToast({ type: 'error', message: '❌ Cover image must be under 10 MB.' });
      return;
    }
    setCoverFile(file);
    setCoverPreview(URL.createObjectURL(file));
  }, []);

  const clearCover = useCallback(() => {
    setCoverFile(null);
    setCoverPreview('');
    if (coverInputRef.current) coverInputRef.current.value = '';
  }, []);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  }, []);

  const addGalleryUrl = useCallback(() => {
    const url = urlInput.trim();
    if (!url) return;
    try { new URL(url); } catch { return; }
    if (galleryUrls.includes(url)) return;
    setGallery(prev => [...prev, url]);
    setUrlInput('');
    urlInputRef.current?.focus();
  }, [urlInput, galleryUrls]);

  const removeGalleryUrl = useCallback((url) => {
    setGallery(prev => prev.filter(u => u !== url));
  }, []);

  const validate = useCallback(() => {
    const e = {};
    if (!form.title.trim())    e.title    = 'Title is required.';
    if (!form.location.trim()) e.location = 'Location is required.';
    if (!form.date)            e.date     = 'Date is required.';
    if (!form.district)        e.district = 'Chapter / district is required.';
    return e;
  }, [form]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSubmitting(true);
    setToast(null);

    try {
      // Upload the cover image (if chosen) and grab the download URL.
      let coverImageUrl = '';
      if (coverFile) {
        const ext = (coverFile.name.split('.').pop() || 'jpg').toLowerCase();
        const path = `events/${Date.now()}.${ext}`;
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, coverFile, {
          contentType: coverFile.type,
        });
        coverImageUrl = await getDownloadURL(storageRef);
      }

      await addDoc(collection(db, 'events'), {
        title:           form.title.trim(),
        description:     form.description.trim(),
        location:        form.location.trim(),
        date:            form.date,      // stored as ISO string; app parses it
        time:            form.time,
        district:        form.district,
        status:          form.status,
        cover_image_url: coverImageUrl,
        gallery_urls:    galleryUrls,
        attendees:       [],
        attendee_count:  0,
        created_at:      serverTimestamp(),
      });

      setToast({ type: 'success', message: `✅ "${form.title.trim()}" created successfully!` });
      setForm(INITIAL);
      setGallery([]);
      clearCover();
    } catch (err) {
      setToast({ type: 'error', message: `❌ Failed: ${err.message}` });
    } finally {
      setSubmitting(false);
    }
  }, [form, galleryUrls, validate]);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Add Event</h1>
          <p className="page-subtitle">Create a new ride, meetup, or community event.</p>
        </div>
      </div>

      {toast && (
        <div className={`amf-toast amf-toast--${toast.type}`} style={{ marginBottom: 24 }}>
          {toast.message}
          <button className="amf-toast-close" onClick={() => setToast(null)}>×</button>
        </div>
      )}

      <form className="aep-form" onSubmit={handleSubmit} noValidate>
        <div className="aep-grid">

          {/* Left column */}
          <div className="aep-col">
            <Field label="Event Title" required error={errors.title}>
              <input name="title" className={`amf-input ${errors.title ? 'amf-input--error' : ''}`}
                placeholder="e.g. Calicut Chapter Monsoon Ride"
                value={form.title} onChange={handleChange} />
            </Field>

            <Field label="Description">
              <textarea name="description" className="amf-input aep-textarea"
                placeholder="Tell members what to expect…"
                rows={4} value={form.description} onChange={handleChange} />
            </Field>

            <Field label="Location" required error={errors.location}>
              <input name="location" className={`amf-input ${errors.location ? 'amf-input--error' : ''}`}
                placeholder="e.g. Wayanad Ghats, Calicut"
                value={form.location} onChange={handleChange} />
            </Field>

            <div className="aep-row">
              <Field label="Date" required error={errors.date} style={{ flex: 1 }}>
                <input name="date" type="date" className={`amf-input ${errors.date ? 'amf-input--error' : ''}`}
                  value={form.date} onChange={handleChange} />
              </Field>
              <Field label="Time" style={{ flex: 1 }}>
                <input name="time" type="time" className="amf-input"
                  value={form.time} onChange={handleChange} />
              </Field>
            </div>
          </div>

          {/* Right column */}
          <div className="aep-col">
            <Field label="Chapter / District" required error={errors.district}>
              <select name="district"
                className={`amf-select ${errors.district ? 'amf-input--error' : ''}`}
                value={form.district} onChange={handleChange}>
                <option value="" disabled>Select chapter…</option>
                {DISTRICTS.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </Field>

            <Field label="Status">
              <div className="aep-status-pills">
                {STATUSES.map(s => (
                  <button
                    key={s} type="button"
                    className={`aep-pill ${form.status === s ? `aep-pill--${s}` : ''}`}
                    onClick={() => setForm(prev => ({ ...prev, status: s }))}
                  >
                    {s.toUpperCase()}
                  </button>
                ))}
              </div>
            </Field>

            <Field label="Cover Image">
              {coverPreview ? (
                <div style={{
                  position: 'relative',
                  borderRadius: 8,
                  overflow: 'hidden',
                  border: '1px solid rgba(212, 181, 116, 0.25)',
                }}>
                  <img src={coverPreview} alt="Preview"
                    style={{
                      width: '100%', maxHeight: 200,
                      objectFit: 'cover', display: 'block',
                    }} />
                  <button type="button" onClick={clearCover}
                    style={{
                      position: 'absolute', top: 8, right: 8,
                      padding: '6px 12px',
                      background: 'rgba(0,0,0,0.75)',
                      border: '1px solid #E63946',
                      borderRadius: 6,
                      color: '#E63946', fontSize: 12, fontWeight: 600,
                      cursor: 'pointer',
                    }}>Remove</button>
                  <div style={{
                    padding: '8px 12px',
                    fontSize: 11,
                    color: '#B79F6D',
                    background: '#0F0F0F',
                    fontFamily: 'monospace',
                  }}>
                    {coverFile?.name} · {coverFile && (coverFile.size / 1024).toFixed(0)} KB
                  </div>
                </div>
              ) : (
                <button type="button"
                  onClick={() => coverInputRef.current?.click()}
                  style={{
                    width: '100%',
                    padding: '24px 16px',
                    background: '#161618',
                    border: '2px dashed rgba(212, 181, 116, 0.35)',
                    borderRadius: 8,
                    color: '#D8C8A4', fontSize: 13,
                    cursor: 'pointer',
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', gap: 6,
                  }}>
                  <span style={{ fontSize: 24 }}>📷</span>
                  <span style={{ fontWeight: 700 }}>Click to upload cover</span>
                  <span style={{ color: '#B79F6D', fontSize: 11, textAlign: 'center', lineHeight: 1.4 }}>
                    JPG, PNG, or WebP · max 10 MB
                    <br />Recommended: <strong>16:9 · ~1200×675 px</strong>
                  </span>
                </button>
              )}
              <input
                ref={coverInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleCoverChange}
              />
            </Field>

            {/* Gallery URL builder */}
            <Field label={`Gallery Photos (${galleryUrls.length})`}>
              <div className="aep-gallery-input-row">
                <input
                  ref={urlInputRef}
                  type="url"
                  className="amf-input"
                  placeholder="Paste a photo URL and press Enter"
                  value={urlInput}
                  onChange={e => setUrlInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addGalleryUrl(); } }}
                />
                <button type="button" className="aep-add-url-btn" onClick={addGalleryUrl}>
                  Add
                </button>
              </div>

              {galleryUrls.length > 0 && (
                <ul className="aep-gallery-list">
                  {galleryUrls.map((url, i) => (
                    <li key={url} className="aep-gallery-item">
                      <span className="aep-gallery-index">{i + 1}</span>
                      <span className="aep-gallery-url" title={url}>{url}</span>
                      <button
                        type="button"
                        className="aep-gallery-remove"
                        onClick={() => removeGalleryUrl(url)}
                        aria-label="Remove"
                      >
                        ×
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </Field>
          </div>
        </div>

        {/* Submit */}
        <div className="aep-submit-row">
          <button type="submit" className="amf-submit-btn" disabled={submitting}>
            {submitting ? <><span className="amf-spinner" /> Publishing…</> : 'Publish Event'}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, required, error, children, style }) {
  return (
    <div className="amf-field" style={style}>
      <label className="amf-label">
        {label}
        {required && <span className="amf-required"> *</span>}
      </label>
      {children}
      {error && <p className="amf-error-text">{error}</p>}
    </div>
  );
}
